import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import TicketDialog from '../ticket/TicketDialog';
import { catalogueApi, errorOf } from '../../lib/catalogue';
import { fmtDate } from '../../lib/when';
import '../../pages/catalogue/Catalogue.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * THE REQUEST ON A TICKET (migration 124): when a ticket was raised from the
 * catalogue, what was asked for - the item, the answers, the cost, who
 * approves it and why - and, once it is approved, its CHECKLIST: each step
 * with its owner, ticked here or from the owner's to-dos (either ticks the
 * other). The owner ticks their own; an administrator may tick, skip (with a
 * reason), hand a step to somebody else, or run a Jarvis action again.
 *
 * Nothing at all on any other ticket, nor on a server without 124: the call
 * answers no request, or a 503, and the panel stays away.
 */
export default function RequestPanel({ ticketId, me, auth, contractor = false, onChanged }) {
  const [req, setReq] = useState(null);
  const [busy, setBusy] = useState(null);
  const [problem, setProblem] = useState(null);
  const [skipping, setSkipping] = useState(null);
  const [why, setWhy] = useState('');

  const load = useCallback(() => catalogueApi.ticket(ticketId, auth).then(setReq).catch(() => setReq(null)), [ticketId, auth]);
  useEffect(() => { load(); }, [load]);

  if (!req) return null;
  const admin = Boolean(me?.isAdmin) && !contractor;
  const mine = (s) => me?.id && s.owner_id === me.id && !contractor;

  const act = async (key, fn) => {
    setBusy(key); setProblem(null);
    try {
      const out = await fn();
      if (out && out.steps) setReq(out); else await load();
      if (onChanged) onChanged();
    } catch (e) {
      setProblem(errorOf(e, 'That did not go through.'));
      await load();
    } finally {
      setBusy(null);
    }
  };
  const tick = (s, done) => act(s.id, () => catalogueApi.step(ticketId, s.id, { state: done ? 'done' : 'todo' }));
  const run = (s) => act(s.id, async () => {
    const r = await catalogueApi.run(ticketId, s.id, { allow_over: true }).catch((e) => e?.response?.data?.request ? e.response.data : Promise.reject(e));
    return r.request;
  });
  const skip = (e) => {
    e.preventDefault();
    const s = skipping;
    if (!why.trim()) return;
    setSkipping(null);
    act(s.id, () => catalogueApi.step(ticketId, s.id, { state: 'skipped', reason: why.trim() }));
  };

  const answers = (req.answers || []).filter((a) => a.display !== '' && a.display !== null && a.display !== undefined);
  return (
    <section className="rq-ticket" aria-label="Catalogue request" data-testid="request-panel">
      <div className="rq-ticket-head">
        <h2>{`Requested: ${req.item_name}`}</h2>
        {req.cost_words && <span className="rq-stage" data-tone="open">{req.cost_words}</span>}
      </div>
      {answers.length > 0 && (
        <dl className="rq-answers">
          {answers.map((a) => (
            <React.Fragment key={a.field_id}><dt>{a.label}</dt><dd>{a.display}</dd></React.Fragment>
          ))}
        </dl>
      )}
      <p className="rq-row-sub">{req.approval_said}</p>
      {req.steps.length === 0 && req.approval_status !== 'approved' && (
        <p className="rq-row-sub">The checklist starts once it is approved.</p>
      )}
      {req.steps.length > 0 && (
        <>
          <p className="rq-side-label">{`Checklist - ${req.steps_done} of ${req.steps_total} done`}</p>
          <ul className="rq-steps">
            {req.steps.map((s) => {
              const canTick = s.kind === 'step' ? (admin || mine(s)) : admin;
              return (
                <li key={s.id} className="rq-step" data-state={s.state} data-step={s.title}>
                  <input type="checkbox" aria-label={s.title} checked={s.state !== 'todo'} disabled={!canTick || busy === s.id || s.state === 'skipped'}
                         onChange={(e) => tick(s, e.target.checked)} />
                  <div className="rq-step-body">
                    <div className="rq-step-title">{s.title}</div>
                    <div className="rq-step-sub">
                      {[s.kind === 'step' ? (s.owner_name || 'Nobody yet') : 'Jarvis',
                        s.state !== 'todo' && s.done_at ? `${s.state === 'skipped' ? 'skipped' : 'done'} ${fmtDate(s.done_at)}${s.done_by_name ? ` by ${s.done_by_name}` : ''}` : null]
                        .filter(Boolean).join(' · ')}
                    </div>
                    {s.notes && <div className="rq-step-sub">{s.notes}</div>}
                    {s.outcome && <div className="rq-step-sub">{s.outcome}</div>}
                    {admin && s.state === 'todo' && (
                      <div className="rq-step-tools">
                        {s.kind !== 'step' && <button type="button" className="btn btn-plain btn-small" disabled={busy === s.id} onClick={() => run(s)}>Try again</button>}
                        <button type="button" className="btn btn-plain btn-small" onClick={() => { setSkipping(s); setWhy(''); }}>Skip…</button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
      {problem && <p className="rq-field-error" role="alert">{problem}</p>}
      {skipping && (
        <TicketDialog label={`Skip “${skipping.title}”`} onClose={() => setSkipping(null)}>
          <form className="tk-form" onSubmit={skip}>
            <label className="tk-label" htmlFor="rq-skip-why">Why is it not needed?</label>
            <textarea id="rq-skip-why" className="tk-input" rows={2} value={why} onChange={(e) => setWhy(e.target.value)} />
            <div className="tk-row">
              <button type="submit" className="tk-btn tk-btn-primary" disabled={!why.trim()}>Skip it</button>
              <button type="button" className="tk-btn tk-btn-quiet" onClick={() => setSkipping(null)}>Cancel</button>
            </div>
          </form>
        </TicketDialog>
      )}
    </section>
  );
}

/**
 * "2 STEPS OF THE CHECKLIST ARE STILL OPEN": what the server said when Mark
 * resolved was pressed on a catalogue ticket with steps left (code
 * 'steps_open'), and the two ways on - resolve anyway with a reason (the
 * open steps are skipped, and the History keeps the reason), or Cancel.
 */
export function StepsOpenDialog({ check, id, auth, onClose, onDone }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const send = async (e) => {
    e.preventDefault();
    if (busy || text.trim().length < 3) return;
    setBusy(true); setError(null);
    try {
      await axios.patch(`${API_BASE}/tickets/${id}/status`, { status: 'Resolved', override_reason: text.trim() }, auth);
      onDone('Resolved. The open steps were skipped, and the reason is in the History.');
    } catch (err) {
      setError(err.response?.data?.error || 'That did not go through.');
    } finally {
      setBusy(false);
    }
  };
  const open = check.open || [];
  return (
    <TicketDialog label={open.length === 1 ? 'A step of the checklist is still open' : `${open.length} steps of the checklist are still open`}
                  onClose={onClose} className="tk-resolve-check">
      <ul className="rq-steps" data-testid="steps-open">
        {open.map((s) => <li key={s.title} className="rq-step"><span className="rq-step-title">{s.title}</span>{s.owner_name ? <span className="rq-step-sub">{` - ${s.owner_name}`}</span> : null}</li>)}
      </ul>
      <form className="tk-form" onSubmit={send}>
        <label className="tk-label" htmlFor="rq-resolve-why">Resolve it anyway? Say why</label>
        <textarea id="rq-resolve-why" className="tk-input" rows={3} value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="e.g. They collected it themselves." />
        <p className="tk-quiet tk-small">The open steps are skipped and their to-dos closed. The reason is kept in the History.</p>
        {error && <p className="tk-error" role="alert">{error}</p>}
        <div className="tk-row">
          <button type="submit" className="tk-btn tk-btn-go" disabled={busy || text.trim().length < 3}>{busy ? 'Working…' : 'Resolve anyway'}</button>
          <button type="button" className="tk-btn tk-btn-quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </TicketDialog>
  );
}
